import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Megaphone, Check, X, Loader2, Copy, ChevronDown, FileText } from "lucide-react";

export function PartnerApplications({ onApproved }) {
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState(null);
  const [pct, setPct] = useState({});
  const [creds, setCreds] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/partner-applications"); setApps(data || []); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (a) => {
    setBusy("a-" + a.id);
    try {
      const { data } = await api.post(`/admin/partner-applications/${a.id}/approve`, { commission_pct: Number(pct[a.id] || 50) });
      setCreds(data); toast.success(`Patnè apwouve: ${data.email}`); load(); onApproved && onApproved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };
  const reject = async (id) => {
    setBusy("r-" + id);
    try { await api.post(`/admin/partner-applications/${id}/reject`); toast.success("Rejte"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const pending = apps.filter((a) => a.status === "pending");

  return (
    <div className="border border-[#1C8CFF]/40 bg-[#0F0F0F] rounded-lg overflow-hidden" data-testid="partner-applications">
      <button onClick={() => setOpen((s) => !s)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="flex items-center gap-2 text-sm font-semibold text-[#8fc4ff]"><Megaphone className="h-4 w-4" />
          Aplikasyon Patnè {pending.length > 0 && <span className="ml-1 h-5 min-w-5 px-1.5 rounded-full bg-[#f5a623] text-black text-[10px] font-bold inline-flex items-center justify-center">{pending.length}</span>}</span>
        <ChevronDown className={`h-4 w-4 text-[#8fc4ff] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          {creds && (
            <div className="border border-[#00F593]/40 bg-[#00F593]/5 p-3 text-xs font-mono-term" data-testid="partner-creds">
              <div>Email: <b className="text-white">{creds.email}</b> · Modpas: <b className="text-white">{creds.temp_password}</b>
                <button className="ml-2 align-middle" onClick={async () => { try { await navigator.clipboard.writeText(creds.temp_password); toast.success("Kopye"); } catch { toast.error("Kopye manyèlman"); } }}><Copy className="h-3.5 w-3.5 inline text-[#8fb4e6]" /></button></div>
              <div className="text-[#8fb4e6] mt-0.5">Lyen: {creds.referral?.path_link} · Komisyon: {creds.commission_pct}%</div>
            </div>
          )}
          {pending.length === 0 ? <div className="text-center text-[#888] text-sm py-4">Pa gen aplikasyon annatant.</div> :
            pending.map((a) => (
              <div key={a.id} className="border border-[#222] bg-[#0A0A0A] p-3" data-testid={`partner-app-${a.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="font-heading font-semibold text-sm">{a.name}</div>
                    <div className="font-mono-term text-[11px] text-[#888]">{a.email}{a.phone ? ` · ${a.phone}` : ""}</div>
                    <div className="font-mono-term text-[11px] text-[#1C8CFF] mt-0.5">{a.platform || "—"} · {a.handle || ""} · {a.followers ? `${a.followers} followers` : "followers ?"}</div>
                    {a.message && <p className="text-xs text-[#999] mt-1">{a.message}</p>}
                    {a.payout_email && <div className="font-mono-term text-[10px] text-[#666] mt-0.5">Imèl pou peye: {a.payout_email}</div>}
                    {(a.id_front_url || a.id_back_url || a.account_proof_url) && (
                      <div className="flex flex-wrap gap-2 mt-2" data-testid={`partner-app-docs-${a.id}`}>
                        {a.id_front_url && <a href={a.id_front_url} target="_blank" rel="noreferrer" data-testid={`partner-doc-front-${a.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[#1C8CFF]/10 text-[#1C8CFF] hover:bg-[#1C8CFF]/20"><FileText className="h-3 w-3" /> ID Fas</a>}
                        {a.id_back_url && <a href={a.id_back_url} target="_blank" rel="noreferrer" data-testid={`partner-doc-back-${a.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[#1C8CFF]/10 text-[#1C8CFF] hover:bg-[#1C8CFF]/20"><FileText className="h-3 w-3" /> ID Do</a>}
                        {a.account_proof_url && <a href={a.account_proof_url} target="_blank" rel="noreferrer" data-testid={`partner-doc-account-${a.id}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[#00F593]/10 text-[#00F593] hover:bg-[#00F593]/20"><FileText className="h-3 w-3" /> Kont Reyèl</a>}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <div className="flex items-center gap-1">
                      <Input type="number" value={pct[a.id] ?? 50} onChange={(e) => setPct({ ...pct, [a.id]: e.target.value })}
                        data-testid={`partner-app-pct-${a.id}`} className="h-8 w-16 bg-[#0F0F0F] border-[#222] rounded-sm text-xs" />
                      <span className="text-[10px] text-[#888]">%</span>
                    </div>
                    <Button size="sm" onClick={() => approve(a)} disabled={busy === "a-" + a.id} data-testid={`approve-partner-${a.id}`}
                      className="h-8 rounded-sm bg-[#00F593] text-black hover:bg-[#00d97f] text-xs font-semibold"><Check className="h-4 w-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => reject(a.id)} disabled={busy === "r-" + a.id} data-testid={`reject-partner-${a.id}`}
                      className="h-8 w-8 p-0 rounded-sm text-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30]"><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
